// Display helpers for a scaling run on Forecasting > Scaling Results. Pooled
// dimensions arrive as "" in `{id}_scaling.json` and read "All platforms" /
// "All tactics"; an unlabelled tactic ("N/A") reads "Untagged".
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { instantToUtcTimestamp } from '../../../utils/date-utils';
import { pooledPlatformLabel, pooledTacticLabel } from '../forecasting-utils';
import { ScalingAnalysis, ScalingAnalysisStatus } from '../scalingAnalysisContract';
import {
  COMBO_SEPARATOR,
  comboOptions,
  ScalingFixture,
  ScalingRunFile,
  splitComboValue
} from './scalingData';

/** `Paid Social / All platforms / All tactics`. */
export const scalingComboLabel = (channel: string, platform: string, tactic: string): string =>
  [channel, pooledPlatformLabel(platform), pooledTacticLabel(tactic)].join(' / ');

/** The run's combo selector, labelled; values stay `comboOptions`' raw keys. */
export const comboOptionItems = (run: ScalingFixture): MenuItem<string>[] =>
  comboOptions(run).map(value => {
    const { platform, tactic } = splitComboValue(value);
    return { value, label: scalingComboLabel(run.channel, platform, tactic) };
  });

/** The selector value for a run's combo (the inverse of splitComboValue). */
export const comboValueFor = (run: ScalingFixture, platform: string, tactic: string): string =>
  [run.channel, platform, tactic].join(COMBO_SEPARATOR);

/** The top of a finished run's curve: econ's `spendCeiling` (which differs from
 *  the request when econ fell back to 1.5x the highest week), else the request. */
export const runSpendCeiling = (run: ScalingRunFile): number | undefined => {
  const ceilings = run.combos
    .flatMap(c => Object.values(c.outcomes))
    .map(o => o?.spendCeiling)
    .filter((n): n is number => Number.isFinite(n));
  if (ceilings.length > 0) {
    return Math.max(...ceilings);
  }
  const requested = run.request?.maxWeeklySpend;
  return Number.isFinite(requested) ? requested : undefined;
};

/** Did econ ignore the requested max and use its 1.5x-highest-week ceiling? */
export const usedAutoCeiling = (run: ScalingRunFile): boolean =>
  run.combos.some(c => Object.values(c.outcomes).some(o => o?.ceilingSource === 'auto_1.5x'));

const formatPerWeek = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}/wk`;

const STATUS_SUFFIX: Record<ScalingAnalysisStatus, string> = {
  [ScalingAnalysisStatus.COMPLETED]: '',
  [ScalingAnalysisStatus.IN_PROGRESS]: ' · In progress',
  [ScalingAnalysisStatus.FAILED]: ' · Failed'
};

/**
 * A run in the selector, e.g. `Paid Social / Meta / Traffic — Store Purchase ·
 * up to $150,000/wk · 2026-10-08 14:02 UTC`, with ` · In progress` / ` · Failed`
 * for runs that haven't completed. The max is the requested one (the list
 * doesn't carry econ's ceiling); see runSpendCeiling for a loaded run.
 */
export const scalingRunLabel = (run: ScalingAnalysis): string => {
  const combo = `${scalingComboLabel(run.channel, run.platform ?? '', run.tactic ?? '')} — ${run.kpi}`;
  const max =
    run.maxWeeklySpend === null ? 'auto max' : `up to ${formatPerWeek(run.maxWeeklySpend)}`;
  const createdAtMs = Date.parse(run.createdAt);
  const when = Number.isNaN(createdAtMs) ? '' : ` · ${instantToUtcTimestamp(createdAtMs)}`;
  return `${combo} · ${max}${when}${STATUS_SUFFIX[run.status] ?? ''}`;
};

/** The line under the selector once a run's curve is loaded. */
export const spendCeilingNote = (run: ScalingRunFile): string | null => {
  const ceiling = runSpendCeiling(run);
  if (ceiling === undefined) {
    return null;
  }
  return usedAutoCeiling(run)
    ? `Curve runs to ${formatPerWeek(ceiling)} (auto: 1.5x your highest week).`
    : `Curve runs to ${formatPerWeek(ceiling)}.`;
};
